import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, LayoutGroup, motion } from "motion/react";
import { ArrowRight, ArrowUpRight, Globe, Menu, RotateCcw, X } from "lucide-react";
import { useCountry } from "../../contexts/CountryContext";
import { useAuthModal } from "../../contexts/AuthModalContext";
import { Logo } from "../brand/Logo";
import { EASE_EXPO, Magnetic, SPRING_POP } from "../motion/primitives";
import { useMember } from "../community/parts";
import { LANGUAGES } from "../../data/languages";

const INK = "#0A0F0E";
const BRAND_DARK = "#00332F";
const TEAL = "#2EE6D6";
const TEAL_SOFT = "#CFF9F4";

/** One list for the desktop bar and the mobile menu, so they cannot drift. */
const NAV_LINKS = [
  { label: "Community", href: "/community" },
  { label: "Guides", href: "/guides" },
  { label: "Blog", href: "/blog" },
];

/** The one action on every page: the plan form. */
const PLAN_LINK = "/start";

/**
 * The other languages, each to its own front door (today /ru, a whole check in
 * Russian). A link, not a toggle: most pages have no Russian twin, and a
 * toggle would promise one.
 */
const OTHER_LANGUAGES = LANGUAGES.filter((l) => l.code !== "en");

/**
 * Site navigation. At the top of a page it sits flat on the background; once
 * the page scrolls it lifts into a floating white pill. The outer bar is always
 * 73px tall, which the homepage hero's height calculation relies on.
 *
 * Desktop: links with a hover highlight that slides between them, "Sign in"
 * for existing accounts and the plan button. Phones: a full-screen menu with
 * large links.
 */
export function Navbar() {
  const { isConfirmed, resetSelections } = useCountry();
  const { openSignIn } = useAuthModal();
  const member = useMember();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the phone menu on navigation and with Escape; stop the page scrolling under it.
  useEffect(() => setMenuOpen(false), [location.pathname, location.hash]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setMenuOpen(false);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const isActive = (href: string) => location.pathname === href || location.pathname.startsWith(`${href}/`);

  const logoClick = (e: React.MouseEvent) => {
    if (isHomePage) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <>
      <nav
        aria-label="Main"
        className="sticky top-0 z-[100] flex h-[73px] items-center px-3 transition-colors duration-300 sm:px-5"
        // The homepage hero is white (Wise's rhythm); the bar matches it until the pill takes over.
        style={{ backgroundColor: isHomePage && !scrolled ? "#FFFFFF" : "transparent" }}
      >
        <motion.div
          className="mx-auto flex w-full items-center justify-between"
          initial={false}
          animate={
            scrolled
              ? { maxWidth: 1120, height: 56, paddingLeft: 10, paddingRight: 8, borderRadius: 999, backgroundColor: "rgba(255,255,255,0.94)", boxShadow: "0 1px 2px rgba(10,15,14,0.05), 0 12px 32px rgba(10,15,14,0.10)" }
              : { maxWidth: 1240, height: 64, paddingLeft: 12, paddingRight: 12, borderRadius: 999, backgroundColor: "rgba(255,255,255,0)", boxShadow: "0 0 0 rgba(10,15,14,0), 0 0 0 rgba(10,15,14,0)" }
          }
          transition={{ duration: 0.45, ease: EASE_EXPO }}
          style={{ backdropFilter: scrolled ? "blur(14px) saturate(1.4)" : "none", WebkitBackdropFilter: scrolled ? "blur(14px) saturate(1.4)" : "none" }}
        >
          <Link to="/" onClick={logoClick} aria-label="Migrada, home" className="group flex items-center">
            <motion.span whileHover={{ rotate: -6, scale: 1.05 }} transition={SPRING_POP} className="inline-flex">
              <Logo size={scrolled ? 36 : 38} />
            </motion.span>
          </Link>

          {/* Desktop */}
          <div className="hidden items-center gap-1 lg:flex">
            <LayoutGroup id="nav">
              <ul className="flex items-center" onMouseLeave={() => setHovered(null)}>
                {NAV_LINKS.map((item) => {
                  const active = isActive(item.href);
                  return (
                    <li key={item.href} className="relative" onMouseEnter={() => setHovered(item.href)}>
                      {hovered === item.href && (
                        <motion.span layoutId="nav-hover" className="absolute inset-0 rounded-full" style={{ backgroundColor: TEAL_SOFT }} transition={SPRING_POP} />
                      )}
                      <Link
                        to={item.href}
                        aria-current={active ? "page" : undefined}
                        className="relative block px-4 py-2 text-[15px] font-bold tracking-[-0.01em] transition-colors"
                        style={{ color: active || hovered === item.href ? BRAND_DARK : "rgba(10,15,14,0.62)" }}
                      >
                        {item.label}
                        {active && (
                          <motion.span
                            layoutId="nav-active"
                            className="absolute bottom-[3px] left-1/2 h-[5px] w-[5px] -translate-x-1/2 rounded-full"
                            style={{ backgroundColor: TEAL }}
                            transition={SPRING_POP}
                          />
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </LayoutGroup>

            <span className="mx-2 h-5 w-px" style={{ backgroundColor: "rgba(10,15,14,0.12)" }} aria-hidden="true" />

            {OTHER_LANGUAGES.map((l) => (
              <Link
                key={l.code}
                to={l.path}
                lang={l.code}
                hrefLang={l.code}
                title={l.name}
                aria-label={l.name}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-[14px] font-bold uppercase transition-colors hover:bg-[#F2F2ED]"
                style={{ color: "rgba(10,15,14,0.62)" }}
              >
                <Globe className="h-4 w-4" aria-hidden="true" />
                {l.code}
              </Link>
            ))}

            {member ? (
              <Link to="/app" className="rounded-full px-4 py-2 text-[15px] font-bold transition-colors hover:bg-[#F2F2ED]" style={{ color: "rgba(10,15,14,0.62)" }}>
                My account
              </Link>
            ) : (
              <button
                type="button"
                onClick={openSignIn}
                className="rounded-full px-4 py-2 text-[15px] font-bold transition-colors hover:bg-[#F2F2ED]"
                style={{ color: "rgba(10,15,14,0.62)" }}
              >
                Sign in
              </button>
            )}

            <Magnetic strength={0.18}>
              <Link
                to={PLAN_LINK}
                className="group ml-1 inline-flex items-center gap-1.5 rounded-full px-5 py-3 text-[15px] font-extrabold transition-[filter,transform] hover:brightness-95 active:scale-95"
                style={{ backgroundColor: TEAL, color: BRAND_DARK }}
              >
                Get visa help
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </Link>
            </Magnetic>
          </div>

          {/* Phones and tablets */}
          <div className="flex items-center gap-2 lg:hidden">
            <AnimatePresence>
              {isConfirmed && (
                <motion.button
                  type="button"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => {
                    resetSelections();
                    navigate("/");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-[#0A0F0E]/[0.06]"
                  aria-label="Reset country"
                >
                  <RotateCcw className="h-4 w-4" style={{ color: "rgba(10,15,14,0.55)" }} />
                </motion.button>
              )}
            </AnimatePresence>
            <Link
              to={PLAN_LINK}
              className="hidden rounded-full px-4 py-2.5 text-[14px] font-extrabold sm:inline-flex"
              style={{ backgroundColor: TEAL, color: BRAND_DARK }}
            >
              Get visa help
            </Link>
            <motion.button
              type="button"
              whileTap={{ scale: 0.9 }}
              onClick={() => setMenuOpen(true)}
              className="flex h-11 w-11 items-center justify-center rounded-full text-white"
              style={{ backgroundColor: INK }}
              aria-label="Open menu"
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
            >
              <Menu className="h-5 w-5" />
            </motion.button>
          </div>
        </motion.div>
      </nav>

      {/* Phone menu: full screen, ink, big type. */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            id="mobile-menu"
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
            className="fixed inset-0 z-[300] flex flex-col overflow-y-auto px-5 pb-8 pt-3 sm:px-8 lg:hidden"
            style={{ backgroundColor: INK }}
            initial={{ clipPath: "circle(0% at calc(100% - 42px) 36px)" }}
            animate={{ clipPath: "circle(150% at calc(100% - 42px) 36px)" }}
            exit={{ clipPath: "circle(0% at calc(100% - 42px) 36px)" }}
            transition={{ duration: 0.55, ease: EASE_EXPO }}
          >
            <div className="flex h-[58px] items-center justify-between">
              <Link to="/" onClick={logoClick} aria-label="Migrada, home">
                <Logo tone="teal" size={36} wordColor="#FFFFFF" />
              </Link>
              <motion.button
                type="button"
                whileTap={{ scale: 0.9 }}
                onClick={() => setMenuOpen(false)}
                className="flex h-11 w-11 items-center justify-center rounded-full"
                style={{ backgroundColor: TEAL, color: BRAND_DARK }}
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </motion.button>
            </div>

            <ul className="mt-10 space-y-1">
              {NAV_LINKS.map((item, i) => (
                <li key={item.href} className="overflow-hidden">
                  <motion.div
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 0.7, delay: 0.12 + i * 0.07, ease: EASE_EXPO }}
                  >
                    <Link
                      to={item.href}
                      className="group flex items-center justify-between py-2 font-black uppercase leading-[0.95] tracking-[-0.045em]"
                      style={{ fontSize: "clamp(2.6rem, 12vw, 4rem)", color: isActive(item.href) ? TEAL : "#FFFFFF" }}
                    >
                      {item.label}
                      <ArrowUpRight className="h-8 w-8 transition-transform group-active:translate-x-1 group-active:-translate-y-1" style={{ color: TEAL }} aria-hidden="true" />
                    </Link>
                  </motion.div>
                </li>
              ))}
            </ul>

            <motion.div
              className="mt-auto space-y-3 pt-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35, ease: EASE_EXPO }}
            >
              <Link
                to={PLAN_LINK}
                className="flex w-full items-center justify-center gap-2 rounded-full py-4 text-[18px] font-extrabold"
                style={{ backgroundColor: TEAL, color: BRAND_DARK }}
              >
                Get visa help
                <ArrowRight className="h-5 w-5" aria-hidden="true" />
              </Link>
              {member ? (
                <Link to="/app" className="block w-full rounded-full border py-4 text-center text-[16px] font-bold text-white" style={{ borderColor: "rgba(255,255,255,0.2)" }}>
                  My account
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    openSignIn();
                  }}
                  className="w-full rounded-full border py-4 text-[16px] font-bold text-white"
                  style={{ borderColor: "rgba(255,255,255,0.2)" }}
                >
                  Sign in
                </button>
              )}
              {OTHER_LANGUAGES.map((l) => (
                <Link key={l.code} to={l.path} lang={l.code} hrefLang={l.code} className="flex items-center justify-center gap-2 py-2 text-[16px] font-bold" style={{ color: TEAL }}>
                  <Globe className="h-4 w-4" aria-hidden="true" />
                  {l.name}
                </Link>
              ))}
              <p className="pt-1 text-center text-[13px]" style={{ color: "rgba(255,255,255,0.5)" }}>
                Help for students moving to Austria
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
